import { useState, useEffect, useCallback } from 'react';
import api from '@/api/axios';
import { Card } from '@/components/ui/card';
import { ArrowsLeftRight, Trash, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const AntesDespuesManager = ({ productos }) => {
  const keys = Object.keys(productos);
  const [items, setItems] = useState([]);
  const [producto, setProducto] = useState(keys[0]);
  const [caption, setCaption] = useState('');
  const [antes, setAntes] = useState(null);
  const [despues, setDespues] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const r = await api.get('/landing/antes-despues');
      setItems(r.data.items || []);
    } catch { toast.error('Error cargando antes y después'); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const pick = (setter) => (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Solo imágenes'); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error('Máx 15MB'); return; }
    setter(f);
  };

  const guardar = async () => {
    if (!antes || !despues) { toast.error('Selecciona la foto de ANTES y la de DESPUÉS'); return; }
    try {
      setSaving(true);
      const fd = new FormData();
      fd.append('antes', antes);
      fd.append('despues', despues);
      fd.append('caption', caption.trim());
      fd.append('producto', producto);
      await api.post('/landing/admin/antes-despues', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Antes y después publicado en la galería');
      setAntes(null); setDespues(null); setCaption('');
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando');
    } finally { setSaving(false); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este antes y después?')) return;
    try {
      await api.delete(`/landing/admin/antes-despues/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success('Eliminado');
    } catch { toast.error('Error eliminando'); }
  };

  const FileBtn = ({ label, file, onChange, testid }) => (
    <label data-testid={testid}
      className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg border cursor-pointer transition-colors ${file ? 'border-emerald-400/60 text-emerald-400' : 'border-white/20 text-white/70 hover:border-white/50'}`}>
      <UploadSimple size={14} /> {file ? `${label} ✓` : label}
      <input type="file" accept="image/*" className="hidden" onChange={onChange} />
    </label>
  );

  return (
    <Card className="p-4" data-testid="antes-despues-manager">
      <div className="flex items-center gap-2 mb-4">
        <ArrowsLeftRight size={18} className="text-blue-400" />
        <p className="font-bold text-sm">Antes y Después (Galería del Catálogo)</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={producto} onChange={(e) => setProducto(e.target.value)} data-testid="ad-producto-select"
          style={{ colorScheme: 'dark' }}
          className="bg-black/20 border border-white/15 rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-400 transition-colors">
          {keys.map((k) => <option key={k} value={k}>{productos[k]}</option>)}
        </select>
        <input value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="ad-caption-input"
          placeholder="Descripción (ej: Remodelación de baño en zona 11)"
          className="flex-1 min-w-[180px] bg-black/20 border border-white/15 rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-400 transition-colors" />
        <FileBtn label="Foto ANTES" file={antes} onChange={pick(setAntes)} testid="ad-antes-btn" />
        <FileBtn label="Foto DESPUÉS" file={despues} onChange={pick(setDespues)} testid="ad-despues-btn" />
        <button onClick={guardar} disabled={saving} data-testid="ad-guardar-btn"
          className="text-xs font-black px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-white transition-colors">
          {saving ? 'Publicando…' : 'Publicar'}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="ad-items-grid">
        {items.map((it) => (
          <div key={it.id} className="relative group rounded-xl overflow-hidden border border-white/10 bg-black/30">
            <div className="grid grid-cols-2">
              <div className="relative">
                <img src={it.antes_url} alt="Antes" className="w-full h-24 object-cover" loading="lazy" />
                <span className="absolute bottom-1 left-1 bg-black/70 text-white text-[8px] font-black px-1.5 py-0.5 rounded uppercase">Antes</span>
              </div>
              <div className="relative">
                <img src={it.despues_url} alt="Después" className="w-full h-24 object-cover" loading="lazy" />
                <span className="absolute bottom-1 right-1 bg-blue-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded uppercase">Después</span>
              </div>
            </div>
            <p className="text-[10px] px-2 py-1.5 opacity-70 truncate">{it.caption || 'Sin descripción'} · {productos[it.producto]}</p>
            <button onClick={() => eliminar(it.id)} data-testid={`ad-delete-${it.id}`}
              className="absolute top-1.5 right-1.5 bg-red-500/90 hover:bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash size={12} />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs opacity-50 col-span-full py-3 text-center">Aún no hay pares "antes y después". Sube el primero 👆</p>}
      </div>
      <p className="text-[10px] opacity-40 mt-3">Los pares se muestran en la Galería de /soluciones con un deslizador interactivo para comparar.</p>
    </Card>
  );
};
